import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { QRCodeDisplay } from './QR';
import NumpadButton from './NumpadButton';
import { useButton, useDeviceInfo, useDispatch, useLogger, useSelector } from '~Hooks';
import { generateID } from '~reducers';
import * as actions from '~actions';
import { ORDER_ORIGIN, SERVICE_TYPE } from '@baemingo/order-status-events/dist/types';

const Container = styled.div<{
  isPhone: boolean;
}>`
  height: calc(100vh - 52px - ${({ isPhone }) => (isPhone ? '40px' : '0px')});
  width: 100%;
  background: #0f0f0f;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  background: linear-gradient(to right, rgb(119, 51, 255), rgb(255, 255, 255), rgb(255, 51, 0));
  background-clip: text;
  font-weight: bold;
  font-size: 30px;
  color: rgba(255, 255, 255, 0.2);
`;

const Main = styled.main<{
  disabled?: boolean;
}>`
  flex: 1;
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  transition: all 0.3s;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.5;
    pointer-events: none;
    filter: blur(2px);
    transform: scale(0.9);
  `}
`;

const Footer = styled.footer`
  position: relative;
  z-index: 10;
  padding: 1.5rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ContinueButton = styled.div<{
  touching: boolean;
}>`
  background: rgb(119, 51, 255);
  text-align: center;
  color: #fff;
  font-size: 12px;
  font-weight: bold;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  margin: 0 1rem;
  cursor: pointer;
  transition: background 0.3s;

  ${({ touching }) =>
    touching &&
    `
    background: rgb(255, 51, 0);
    transform: scale(0.95);
    transition: all 0s;
  `}
`;

const Card = styled.div`
  width: calc(100% - 3rem);
  max-width: 24rem;
  padding: 1.5rem;
  margin: 1.5rem;
  background: rgba(20, 20, 20, 0.5);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 0.5rem;
  color: #fff;
`;

const AmountDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const AmountTop = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #aaa;
`;

const AmountText = styled.div`
  display: flex;
  justify-content: center;
  font-size: 3rem;
  font-weight: bold;
  gap: 0.5rem;
  letter-spacing: -0.05em;
`;

const BTC = styled.span`
  color: #007bff;
  font-family: monospace;
  position: relative;
  top: 5px;
`;

const NumpadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  width: 100%;
`;

const PageLayout = () => {
  const dispatch = useDispatch();
  const logger = useLogger('crypto-standalone');
  const deviceInfo = useDeviceInfo();

  /* How much should be charged? Entered by the cashier */
  const [enteredAmount, setEnteredAmount] = useState<number | null>(null);
  const [orderId, setOrderId] = useState(generateID());
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [easyId, setEasyId] = useState<string | null>(null);

  const staffId = useSelector((state) => state.staff?.id);

  // After we have started a payment process, this order will be populated
  const order = useSelector((state) => state.POSOrders.find((o) => o.id === orderId));

  const payment = useSelector((state) => state.POSPayments.find((p) => p.order === orderId));

  const triggerPayment = (digits: string) => {
    const amount = Math.round(parseFloat(digits) * 100);

    if (amount < 1) {
      return;
    }

    setEnteredAmount(amount);

    const currentPaymentId = generateID();

    setPaymentId(currentPaymentId);

    dispatch(
      actions.initiateOrder({
        method: 'CRYPTO',
        serviceType: SERVICE_TYPE.EAT_HERE, //TODO: should be something else?
        id: orderId,
        orderNumber: '123',
        staffId: staffId,
        origin: ORDER_ORIGIN.POS,
      }),
    );

    logger.info(`CRYPTO-STANDALONE: Initiating payment with CRYPTO.`, {
      id: orderId,
      amount: enteredAmount,
      paymentId: currentPaymentId,
    });
  };

  // When the order has been created, create a payment right away
  useEffect(() => {
    if (!order || !enteredAmount || !paymentId) {
      return;
    }

    dispatch(
      actions.initiatePayment(orderId, {
        id: paymentId,
        method: 'CRYPTO',
        amount: enteredAmount,
        canCancel: true,
      }),
    );
  }, [order && order.id]);

  useEffect(() => {
    if (payment?.status === 'FAILED' || payment?.status === 'COMPLETED') {
      setTimeout(() => {
        setPaymentId(null);
        setOrderId(generateID());
        setEnteredAmount(null);

        logger.warn(`Payment canceled ${payment.id}`);

        dispatch({
          type: 'REMOVE_POS_PAYMENT',
          id: payment.id,
        });
      }, 2000);
    }
  }, [payment?.status]);

  const [amount, setAmount] = useState('0');

  const handleNumpadClick = (val: string) => {
    if (val === '⌫') {
      setAmount((prev) => (prev.length > 1 ? prev.slice(0, -1) : '0'));
    } else if (val === '.') {
      if (!amount.includes('.')) setAmount((prev) => prev + '.');
    } else {
      setAmount((prev) => {
        if (prev === '0') return val;
        const newVal = prev + val;
        if (newVal.length > 10) return prev; // Limit to 10 digits
        return newVal;
      });
    }
  };

  const continueButton = useButton({
    onClick: () => {
      if (payment) {
        dispatch(actions.updatePaymentMeta(payment.id, { cancelRequested: true }));
      } else {
        triggerPayment(amount);
      }
    },
  });

  return (
    <Container isPhone={Boolean(deviceInfo?.isPhone)}>
      <Main disabled={!!payment}>
        <Header>Payment Details</Header>
        <Card>
          <AmountDisplay>
            <div>
              <AmountTop>
                <span>Enter Amount</span>
                <span>USD</span>
              </AmountTop>
              <AmountText>
                <BTC>$</BTC>
                <span>{amount}</span>
              </AmountText>
            </div>
          </AmountDisplay>
        </Card>
        <NumpadGrid>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '⌫'].map((val) => (
            <NumpadButton key={val} onClick={handleNumpadClick} value={val} />
          ))}
        </NumpadGrid>
      </Main>

      <Footer>
        <ContinueButton {...continueButton}>
          {payment ? 'Cancel' : 'Generate QR code'}
        </ContinueButton>
      </Footer>

      {payment && <QRCodeDisplay payment={payment} />}
    </Container>
  );
};
export default PageLayout;
